/*
 * Surviving the tab being thrown away.
 *
 * Android discards a background tab whenever another app wants the memory - ten
 * seconds in Gmail is enough - and Chrome reloads it on return. Nothing in this app
 * asks for that reload, and nothing can prevent it. What it can do is make the
 * reload cost nothing: whatever was typed comes back, and the page opens where it
 * was scrolled to.
 *
 * sessionStorage, not localStorage: it is per tab and gone when the tab closes, so a
 * draft never follows anyone to a different session or device.
 */
import { useEffect, useRef, useState } from 'react'

const DRAFT_PREFIX = 'cpd.draft:'
const SCROLL_PREFIX = 'cpd.scroll:'
/* A draft older than this is abandoned, not restored. */
const DRAFT_TTL_MS = 6 * 60 * 60 * 1000

function read<T>(key: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const v = JSON.parse(raw) as { at: number; value: T }
    if (Date.now() - v.at > DRAFT_TTL_MS) { window.sessionStorage.removeItem(key); return null }
    return v.value
  } catch {
    return null
  }
}

function write(key: string, value: unknown) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), value }))
  } catch {
    // Storage full or blocked: the screen still works, it just will not survive a reload.
  }
}

function remove(key: string) {
  try { window.sessionStorage.removeItem(key) } catch { /* nothing to do */ }
}

/**
 * State that survives a reload. Same shape as useState, plus a `clear` for after a
 * successful save so the next visit starts clean. `initial` is used only when there
 * is no draft. Pass a stable `key` per screen and field.
 */
export function useDraft<T>(key: string, initial: T): [T, (next: T | ((prev: T) => T)) => void, () => void] {
  const storageKey = DRAFT_PREFIX + key
  const [value, setValueState] = useState<T>(() => read<T>(storageKey) ?? initial)
  const setValue = (next: T | ((prev: T) => T)) => {
    setValueState((prev) => {
      const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next
      write(storageKey, resolved)
      return resolved
    })
  }
  const clear = () => { remove(storageKey); setValueState(initial) }
  return [value, setValue, clear]
}

/** True when a draft exists for the key, for "seed from server unless typed" logic. */
export function hasDraft(key: string): boolean {
  return read(DRAFT_PREFIX + key) !== null
}

/**
 * Remember where each screen was scrolled to and put it back after a reload.
 *
 * Mount once, above the routes. The position is written on scroll (throttled) and
 * on pagehide, keyed by path. Restoring waits for the page to be tall enough, since
 * the content that made it tall usually arrives from the network a moment later;
 * it gives up after a couple of seconds rather than yanking a page someone has
 * already started reading.
 */
export function useScrollMemory(pathname: string) {
  const restoring = useRef(false)

  useEffect(() => {
    const key = SCROLL_PREFIX + pathname
    const saved = read<number>(key)
    let cancelled = false
    restoring.current = true

    if (saved && saved > 0) {
      const started = Date.now()
      const tryRestore = () => {
        if (cancelled) return
        const max = document.documentElement.scrollHeight - window.innerHeight
        if (max >= saved - 4) {
          window.scrollTo({ top: saved, behavior: 'auto' })
          restoring.current = false
          return
        }
        if (Date.now() - started < 2500) window.setTimeout(tryRestore, 80)
        else restoring.current = false
      }
      tryRestore()
    } else {
      restoring.current = false
    }

    let ticking = false
    const onScroll = () => {
      if (restoring.current || ticking) return
      ticking = true
      window.requestAnimationFrame(() => {
        ticking = false
        write(key, window.scrollY)
      })
    }
    const onHide = () => write(key, window.scrollY)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('pagehide', onHide)
    document.addEventListener('visibilitychange', onHide)
    return () => {
      cancelled = true
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onHide)
    }
  }, [pathname])
}
